import type { StateCreator } from 'zustand';
import type { StoreType, UiSlice } from '@/types/store';
import type { WarningCode } from '@bioavail/shared-types';
import { uid } from '@/data/factory';

/** URL-hash section used by calc-sidebar's SidebarNav. */
const NAV_SECTION = 'nav';

export const createUiSlice: StateCreator<StoreType, [], [], UiSlice> = (set, get) => ({
  activeScreen: 'calculator',
  collapsedSections: {},
  modeBannerDismissed: false,
  dismissedWarnings: [],
  toasts: [],
  pendingLoadDemo: false,

  /**
   * Updates the active screen in the store AND pushes the new URL hash
   * (#nav/<screen>) so calc-sidebar's SidebarNav switches panels.
   * A popstate event is dispatched so SidebarNav's useHashNav listener
   * picks up the change.
   */
  setActiveScreen: (screen) => {
    set({ activeScreen: screen });
    if (typeof window !== 'undefined') {
      const newHash = `#${NAV_SECTION}/${screen}`;
      if (window.location.hash !== newHash) {
        history.pushState(null, '', newHash);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  },

  toggleSection: (key) =>
    set({
      collapsedSections: {
        ...get().collapsedSections,
        [key]: !get().collapsedSections[key],
      },
    }),

  setSection: (key, open) =>
    set({
      collapsedSections: { ...get().collapsedSections, [key]: !open },
    }),

  dismissModeBanner: () => set({ modeBannerDismissed: true }),

  dismissWarning: (code: WarningCode) => {
    if (!get().dismissedWarnings.includes(code)) {
      set({ dismissedWarnings: [...get().dismissedWarnings, code] });
    }
  },

  pushToast: (message, tone = 'info') => {
    const id = uid('toast');
    set({ toasts: [...get().toasts, { id, message, tone }] });
    setTimeout(() => {
      set({ toasts: get().toasts.filter((t) => t.id !== id) });
    }, 4000);
  },

  removeToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  requestLoadDemo: () => set({ pendingLoadDemo: true }),

  consumeLoadDemoRequest: () => set({ pendingLoadDemo: false }),
});
